// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import {
  ActionType,
  IAction,
  IActionTypeChangeSite,
  IActionTypeChangeUser,
  IActionTypeLoginResult,
  IActionTypeParameterLoad,
  IActionTypeSitelist,
  IActionTypeUserlist,
} from "actions";
import { CloudSettings } from "dbtypes";
import { UserRole } from "graphql/api";
import { IStateSite, Site, User } from "states/sitestates";
import { IState } from "states/states";
import { isSet } from "utils";

export default function siteReducer(
  state: IStateSite,
  action: IAction<unknown>,
  rootState: IState,
): IStateSite {
  // type convert action string back into ActionType
  switch (action.type) {
    case ActionType.ACT_LOGIN_SUCC: {
      const payload = action.payload as IActionTypeLoginResult;
      state.user = {
        secId: payload.userId,
        isRfidUser: payload.isRfidUser,
        realName: payload.realName,
        email: payload.email,
      };
      return state;
    }
    case ActionType.ACT_LOGOUT_SUCC:
      state.user = undefined;
      state.siteList = undefined;
      state.userList = undefined;
      return state;
    case ActionType.ACT_FORCE_PWD_CHANGE:
      state.user = {
        email: action.payload as string,
        isRfidUser: false,
      };
      return state;

    case ActionType.ACT_GET_SITES_SUCC: {
      state.siteList = action.payload as IActionTypeSitelist as Site[];
      // also update cached tzOffset entry as this is part of the site list in cloud
      if (isSet(rootState.cached.selectedSite)) {
        state.tzOffset = state.siteList.filter(
          (curSite) => curSite.id === rootState.cached.selectedSite,
        )[0]?.tzOffset;
      }
      return state;
    }

    case ActionType.ACT_GET_USERLIST_BASIC_SUCC:
      state.userList = action.payload as IActionTypeUserlist as User[];
      return state;
    case ActionType.ACT_GET_USERLIST_EXT_SUCC: {
      const newUsers = action.payload as IActionTypeUserlist as User[];
      // we only get a partial list of users, so we need to merge this list with the existing one!
      state.userList = state.userList?.map(
        (curUser) => newUsers.find((newUser) => newUser.id === curUser.id) ?? curUser,
      );
      return state;
    }

    case ActionType.ACT_CHANGE_USER_SUCC: {
      const payload = action.payload as IActionTypeChangeUser;
      // if we could not find the user - a new user was added -> so add this user to our list!
      const curUser = state.userList?.find((curUser) => curUser.id === payload.id) ?? ({} as User);
      if (curUser) {
        // check if we update a new user
        if (!isSet(curUser.id)) {
          // add the new user to the userList!
          state.userList?.push(curUser);
          // only update ID on new users!
          if (isSet(payload.id)) curUser.id = payload.id;
          // in case the frontend has NOT set any role on creating a new user -> the backend
          // will set UserRole.viewer as default -> also set this value here!
          curUser.role = isSet(payload.role) ? payload.role : UserRole.viewer;
        } else {
          // in case we update a user -> set the new role (if it was changed!)
          if (isSet(payload.role)) curUser.role = payload.role;
        }
        // all other values: take into the available or new user-instance!
        if (isSet(payload.name)) curUser.name = payload.name;
        if (isSet(payload.email)) curUser.email = payload.email;
        if (isSet(payload.userSites)) curUser.siteIds = payload.userSites;
        if (isSet(payload.accessRights)) curUser.accessRights = payload.accessRights;
        if (isSet(payload.settings)) curUser.settings = payload.settings;
        if (isSet(payload.chargerControl)) curUser.chargingControl = payload.chargerControl;
      } else {
        console.error("internal error while adding/modifying user", payload);
      }
      return state;
    }

    case ActionType.ACT_CHANGE_SITESETTINGS_SUCC: {
      const payload = action.payload as IActionTypeChangeSite;
      state.siteList
        ?.filter((curSite) => curSite.id === payload.id)
        .forEach((curSite) => {
          if (isSet(payload.cloudSettings)) curSite.cloudSettings = payload.cloudSettings;
        });
      return state;
    }

    case ActionType.ACT_GET_PARAMETER_SUCC: {
      const payload = action.payload as IActionTypeParameterLoad;
      state.tzOffset = payload.tzOffset;
      return state;
    }

    case ActionType.ACT_SAVE_REPORT_SETTINGS_SUCC: // intentional fall-through
    case ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_SUCC: {
      const { id, cloudSettings } = action.payload as { id: number; cloudSettings: CloudSettings };
      const site = state.siteList?.filter((curSite) => curSite.id === id);
      if (site?.length) {
        site[0].cloudSettings = cloudSettings;
      }
      return state;
    }

    default:
      return state;
  }
}
